import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { AlertTriangle, Wand2, Crop, Square, Loader, Check, ChevronDown, ChevronUp, HardDrive, Scissors, Info, Upload, X, RefreshCw, Film, ImagePlus, Plus } from 'lucide-react';
import { mediaAPI, getErrorMessage } from '../../services/api';
import type { UrlMedia } from './types';
import { resolveEffectiveMediaIdForPlatform } from './mediaVariantUtils';

/** Matches the backend ResolutionAction enum (platform_specs.py). */
type MediaResolution = 'auto_fix' | 'user_action' | 'incompatible';
type MediaResolutionAction =
  | 'crop_resize'
  | 'transcode'
  | 'compress'
  | 'trim'
  | 'pad'
  | 'drop_platform'
  | 'change_format'
  | 'replace_media'
  | 'upload_video'
  | 'upload_image'
  | 'upload_additional'
  | 'remove_slide';

interface ComplianceIssue {
  type: string;
  severity: string;
  message: string;
  actual?: number;
  target?: number;
  max?: number;
  fixable?: boolean;
  fix_strategies?: string[];
  suggested_bitrate?: string;
  /** True when the backend has already run a completed auto-fix for this
   * exact (media, platform) pair and the media still doesn't comply.
   * In this state, further Pad/Crop/Trim attempts are futile (the
   * guard in media.py's auto_fix_media will reject them); the UI should
   * suggest a platform-specific upload instead. */
  already_auto_fixed?: boolean;
  /** Three-bucket classification from the backend (canonical source).
   * Falls back to `fixable` + `already_auto_fixed` heuristics when absent
   * so older responses still render correctly. */
  resolution?: MediaResolution;
  actions?: MediaResolutionAction[];
}

interface ComplianceResult {
  media_id: string;
  platform: string;
  content_type: string;
  compliant: boolean;
  issues: ComplianceIssue[];
  media_info: {
    width: number | null;
    height: number | null;
    duration: number | null;
    file_size: number | null;
    media_type: string;
  };
}

interface PendingTask {
  taskId: string;
  mediaId: string;
  platform: string;
  strategy: string;
}

interface TaskStatusResponse {
  task_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  result_media_id?: string;
  result_url?: string;
  result_dimensions?: string;
  error?: string;
}

interface Props {
  urlMediaItems: UrlMedia[];
  setUrlMediaItems: React.Dispatch<React.SetStateAction<UrlMedia[]>>;
  selectedPlatforms: string[];
  setSelectedPlatforms?: React.Dispatch<React.SetStateAction<string[]>>;
  contentType: string;
  setContentType?: (ct: string) => void;
  /** When true, suppress the green "all compliant" banner because preflight already flagged errors */
  hasPreflightErrors?: boolean;
  /** Fires when the user clicks "Replace media" on a user_action / incompatible row.
   *  Composers should respond by scrolling to the media picker and highlighting
   *  the offending item. Optional — falls back to a no-op if omitted. */
  onReplaceMedia?: (mediaId: string, platform: string) => void;
  /** Fires when the user clicks "Upload video"/"Upload image" to swap format
   *  for an incompatible platform/content_type pair. */
  onUploadForPlatform?: (platform: string, kind: 'video' | 'image') => void;
}

/**
 * Runs compliance checks on attached media per-platform and surfaces
 * fixable issues with one-click auto-fix buttons (Onlypult-style).
 * Supports aspect ratio fix (pad/crop) and video re-encode for oversized files.
 * Polls background task status when async fixes are dispatched.
 */
export const MediaComplianceBanner: React.FC<Props> = ({
  urlMediaItems,
  setUrlMediaItems,
  selectedPlatforms,
  setSelectedPlatforms,
  contentType,
  setContentType,
  hasPreflightErrors = false,
  onReplaceMedia,
  onUploadForPlatform,
}) => {
  const { t } = useTranslation();
  const [results, setResults] = useState<ComplianceResult[]>([]);
  const [checking, setChecking] = useState(false);
  const [fixing, setFixing] = useState<string | null>(null); // "mediaId:platform:strategy"
  const [expanded, setExpanded] = useState(true);
  const [pendingTasks, setPendingTasks] = useState<PendingTask[]>([]);
  // Bulk-fix progress state so the user can see how many items are
  // queued, which one is running, and what the final summary looks
  // like. ``null`` means no bulk operation is in flight.
  const [bulkProgress, setBulkProgress] = useState<{
    done: number;
    total: number;
    currentLabel: string;
  } | null>(null);
  const [bulkSummary, setBulkSummary] = useState<{
    succeeded: number;
    failed: number;
    skipped: number;
    failures: Array<{ label: string; reason: string }>;
  } | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const doneMedia = urlMediaItems.filter((m) => m.status === 'done' && m.mediaId);

  const applyResolvedMediaId = useCallback(
    (
      items: UrlMedia[],
      mediaId: string,
      platform: string,
      nextMedia: { mediaId: string; url?: string; filename?: string },
    ): UrlMedia[] =>
      items.map((item) => {
        const effectiveMediaId = resolveEffectiveMediaIdForPlatform(item, platform);
        if (effectiveMediaId !== mediaId) {
          return item;
        }
        if (item.mediaId === mediaId) {
          return {
            ...item,
            id: `fixed-${nextMedia.mediaId}`,
            url: nextMedia.url || item.url,
            filename: nextMedia.filename || item.filename,
            mediaId: nextMedia.mediaId,
          };
        }
        return {
          ...item,
          platformVariants: {
            ...(item.platformVariants || {}),
            [platform]: nextMedia.mediaId,
          },
        };
      }),
    [],
  );

  // ── Compliance checks ──
  const runChecks = useCallback(async () => {
    if (doneMedia.length === 0 || selectedPlatforms.length === 0) {
      setResults([]);
      return;
    }
    setChecking(true);
    try {
      const checks: Promise<ComplianceResult>[] = [];
      for (const media of doneMedia) {
        for (const platform of selectedPlatforms) {
          const effectiveMediaId = resolveEffectiveMediaIdForPlatform(media, platform);
          if (!effectiveMediaId) {
            continue;
          }
          checks.push(
            mediaAPI
              .complianceCheck(effectiveMediaId, platform, contentType)
              .then((r) => r.data as ComplianceResult)
              .catch(() => ({
                media_id: effectiveMediaId,
                platform,
                content_type: contentType,
                compliant: true,
                issues: [],
                media_info: { width: null, height: null, duration: null, file_size: null, media_type: 'unknown' },
              }))
          );
        }
      }
      const all = await Promise.all(checks);
      setResults(all.filter((r) => !r.compliant));
    } finally {
      setChecking(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(doneMedia), JSON.stringify(selectedPlatforms), contentType]);

  useEffect(() => {
    const handle = setTimeout(runChecks, 600);
    return () => clearTimeout(handle);
  }, [runChecks]);

  // Drop the bulk summary once a new compliance run finishes — at that
  // point the banner itself reflects the post-fix state so the summary
  // would just be duplicate information.
  useEffect(() => {
    if (!checking && bulkSummary && results.length === 0) {
      // All clean after fix — auto-dismiss the summary after a moment
      // so the green "all compliant" banner can take over.
      const handle = setTimeout(() => setBulkSummary(null), 2500);
      return () => clearTimeout(handle);
    }
    return undefined;
  }, [checking, bulkSummary, results.length]);

  // ── Task polling ──
  useEffect(() => {
    if (pendingTasks.length === 0) {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      return;
    }

    const poll = async () => {
      const remaining: PendingTask[] = [];
      let anyCompleted = false;

      for (const task of pendingTasks) {
        try {
          const resp = await mediaAPI.taskStatus(task.taskId);
          const data = resp.data as TaskStatusResponse;

          if (data.status === 'completed' && data.result_media_id && data.result_url) {
            // Swap the old media item with the fixed version
            setUrlMediaItems((prev) =>
              applyResolvedMediaId(prev, task.mediaId, task.platform, {
                mediaId: data.result_media_id!,
                url: data.result_url!,
              })
            );
            anyCompleted = true;
            toast.success(
              t('comp_media_fixed', 'Media optimized for {{platform}} ({{strategy}})', {
                platform: task.platform.charAt(0).toUpperCase() + task.platform.slice(1),
                strategy: task.strategy,
              })
            );
          } else if (data.status === 'failed') {
            toast.error(
              data.error || t('comp_media_fix_failed', 'Failed to optimize media')
            );
          } else {
            // Still pending or processing — keep polling
            remaining.push(task);
          }
        } catch {
          // Network error — keep polling
          remaining.push(task);
        }
      }

      setPendingTasks(remaining);
      if (anyCompleted) {
        setTimeout(runChecks, 500);
      }
    };

    pollTimerRef.current = setInterval(poll, 3000);
    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingTasks.length]);

  // ── Three-bucket resolution helpers ──
  //
  // The backend compliance-check endpoint now emits `resolution` and
  // `actions` on every issue. When those are present we trust them
  // directly; otherwise we fall back to the legacy `fixable` +
  // `already_auto_fixed` heuristic so older response shapes still
  // classify correctly.
  const resolutionOf = (issue: ComplianceIssue): MediaResolution => {
    if (issue.resolution) { return issue.resolution; }
    if (issue.fixable) { return 'auto_fix'; }
    if (issue.already_auto_fixed) { return 'user_action'; }
    return 'incompatible';
  };

  // Side-effect router for a user-action / incompatible CTA. Keeps all
  // the "what does Drop platform actually do?" logic co-located.
  const dispatchIssueAction = (
    action: MediaResolutionAction,
    result: ComplianceResult,
  ) => {
    switch (action) {
      case 'drop_platform':
        if (setSelectedPlatforms) {
          setSelectedPlatforms((prev) => prev.filter((p) => p !== result.platform));
          toast.success(
            t('comp_media_dropped_platform', 'Removed {{platform}} from target platforms', {
              platform: result.platform.charAt(0).toUpperCase() + result.platform.slice(1),
            }),
          );
        }
        break;
      case 'change_format':
        // content_type_mismatch is the canonical trigger here — the
        // "safe" target is always `post` since every platform accepts it.
        if (setContentType) {
          setContentType('post');
          toast.success(
            t('comp_media_changed_format', 'Switched content type to post'),
          );
        }
        break;
      case 'replace_media':
        if (onReplaceMedia) {
          onReplaceMedia(result.media_id, result.platform);
        } else if (typeof document !== 'undefined') {
          document.getElementById('studio-media-section')?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
        break;
      case 'upload_video':
      case 'upload_image':
        if (onUploadForPlatform) {
          onUploadForPlatform(result.platform, action === 'upload_video' ? 'video' : 'image');
        } else if (typeof document !== 'undefined') {
          document.getElementById('studio-media-section')?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
        break;
      default:
        // Auto-fix actions (crop_resize, transcode, compress, trim, pad)
        // are driven by the existing handleAutoFix / handleReEncode
        // branches — they're not routed through here.
        break;
    }
  };

  // ── Auto-fix (aspect ratio pad/crop, or duration trim) ──
  // All three strategies share the same sync-over-HTTP pattern — the
  // backend returns the new media record inline. Differentiated only by
  // the button label and the toast copy.
  //
  // ``silent``: when true, suppress per-item toasts so the bulk handler
  // can roll them up into a single summary. Errors always propagate
  // (via throw) so bulk can count failures accurately.
  const handleAutoFix = async (
    mediaId: string,
    platform: string,
    strategy: 'pad' | 'crop' | 'trim',
    options: { silent?: boolean } = {},
  ): Promise<void> => {
    const { silent = false } = options;
    const key = `${mediaId}:${platform}:${strategy}`;
    setFixing(key);
    try {
      const resp = await mediaAPI.autoFix(mediaId, platform, contentType, strategy);
      const data = resp.data as {
        media_id?: string;
        url?: string;
        filename?: string;
        width?: number;
        height?: number;
        already_compliant?: boolean;
        duplicate?: boolean;
        task_id?: string;
        status?: string;
      };

      if (data.already_compliant) {
        if (!silent) {
          toast.success(
            t('comp_media_already_compliant', 'Media already meets requirements'),
          );
        }
        return;
      }

      // Idempotency: server says task already running
      if (data.duplicate && data.task_id) {
        setPendingTasks((prev) => [
          ...prev.filter((t) => t.taskId !== data.task_id),
          { taskId: data.task_id!, mediaId, platform, strategy },
        ]);
        if (!silent) {
          toast(
            t('comp_media_already_processing', 'Fix already in progress...'),
            { icon: '\u23f3' },
          );
        }
        return;
      }

      if (!data.media_id || !data.url) {
        // Throw so callers (especially bulk) can mark this as a failure.
        throw new Error(
          t('comp_media_fix_failed', 'Failed to optimize media'),
        );
      }

      // Replace the old media item with the fixed version
      setUrlMediaItems((prev) =>
        applyResolvedMediaId(prev, mediaId, platform, {
          mediaId: data.media_id!,
          url: data.url!,
          filename: data.filename,
        }),
      );

      if (!silent) {
        toast.success(
          t('comp_media_fixed', 'Media optimized for {{platform}} ({{strategy}})', {
            platform: platform.charAt(0).toUpperCase() + platform.slice(1),
            strategy,
          }),
        );
      }
      setTimeout(runChecks, 300);
    } catch (err) {
      if (!silent) {
        toast.error(
          getErrorMessage(
            err,
            t('comp_media_fix_failed', 'Failed to optimize media'),
          ),
        );
      }
      // Always propagate so the bulk-fix summary can record the failure.
      throw err;
    } finally {
      setFixing(null);
    }
  };

  // ── Video re-encode (async via Celery) ──
  const handleReEncode = async (
    mediaId: string,
    platform: string,
    options: { silent?: boolean } = {},
  ): Promise<void> => {
    const { silent = false } = options;
    const key = `${mediaId}:${platform}:re_encode`;
    setFixing(key);
    try {
      const resp = await mediaAPI.reEncode(mediaId, platform, contentType);
      const data = resp.data as {
        task_id?: string;
        already_compliant?: boolean;
        duplicate?: boolean;
        status?: string;
      };

      if (data.already_compliant) {
        if (!silent) {
          toast.success(
            t('comp_media_already_compliant', 'Video already within size limit'),
          );
        }
        return;
      }

      if (data.duplicate && data.task_id) {
        if (!silent) {
          toast(
            t('comp_media_already_processing', 'Re-encode already in progress...'),
            { icon: '\u23f3' },
          );
        }
      } else if (data.task_id && !silent) {
        toast.success(t('comp_media_re_encoding', 'Video re-encoding started'));
      }

      if (data.task_id) {
        setPendingTasks((prev) => [
          ...prev.filter((t) => t.taskId !== data.task_id),
          { taskId: data.task_id!, mediaId, platform, strategy: 're_encode' },
        ]);
      }
    } catch (err) {
      if (!silent) {
        toast.error(
          getErrorMessage(
            err,
            t('comp_media_reencode_failed', 'Failed to start re-encode'),
          ),
        );
      }
      throw err;
    } finally {
      setFixing(null);
    }
  };

  // ── Render ──
  const hasPendingTasks = pendingTasks.length > 0;

  if (results.length === 0 && !checking && !hasPendingTasks) {
    if (doneMedia.length > 0 && selectedPlatforms.length > 0 && !hasPreflightErrors) {
      return (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs">
          <Check className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="font-medium">
            {t('comp_media_all_compliant', 'Media meets all platform requirements')}
          </span>
        </div>
      );
    }
    return null;
  }

  if (checking && results.length === 0) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-slate-500 text-xs">
        <Loader className="w-3.5 h-3.5 animate-spin flex-shrink-0" />
        <span>{t('comp_media_checking', 'Checking media compliance...')}</span>
      </div>
    );
  }

  const fixableIssues = results.filter((r) =>
    r.issues.some((i) => i.fixable)
  );
  const totalIssues = results.reduce((sum, r) => sum + r.issues.length, 0);

  // Three-bucket counts derived from the compliance results. These feed
  // the header pills so the user sees at a glance how many issues will
  // auto-fix vs need their attention vs are hard blocks.
  let autoFixCount = 0;
  let userActionCount = 0;
  let incompatibleCount = 0;
  for (const r of results) {
    for (const i of r.issues) {
      const bucket = resolutionOf(i);
      if (bucket === 'auto_fix') { autoFixCount++; }
      else if (bucket === 'user_action') { userActionCount++; }
      else { incompatibleCount++; }
    }
  }

  // Label + icon registry for user_action / incompatible CTAs. Mirrors
  // the InlineFormBlock registry — single source across the two surfaces.
  const actionMeta: Record<MediaResolutionAction, {
    label: string;
    icon: React.ElementType;
    className: string;
  }> = {
    crop_resize: { label: t('comp_media_action_crop', 'Crop & resize'), icon: Crop, className: 'bg-amber-600 text-white hover:bg-amber-700' },
    transcode: { label: t('comp_media_action_transcode', 'Transcode'), icon: HardDrive, className: 'bg-blue-600 text-white hover:bg-blue-700' },
    compress: { label: t('comp_media_action_compress', 'Compress'), icon: HardDrive, className: 'bg-blue-600 text-white hover:bg-blue-700' },
    trim: { label: t('comp_media_action_trim', 'Trim'), icon: Scissors, className: 'bg-orange-600 text-white hover:bg-orange-700' },
    pad: { label: t('comp_media_action_pad', 'Pad'), icon: Square, className: 'bg-amber-600 text-white hover:bg-amber-700' },
    drop_platform: { label: t('comp_media_action_drop_platform', 'Drop platform'), icon: X, className: 'bg-white text-red-600 border border-red-300 hover:bg-red-50' },
    change_format: { label: t('comp_media_action_change_format', 'Change format'), icon: RefreshCw, className: 'bg-white text-amber-700 border border-amber-300 hover:bg-amber-100' },
    replace_media: { label: t('comp_media_action_replace_media', 'Replace media'), icon: RefreshCw, className: 'bg-white text-amber-700 border border-amber-300 hover:bg-amber-100' },
    upload_video: { label: t('comp_media_action_upload_video', 'Upload video'), icon: Film, className: 'bg-amber-600 text-white hover:bg-amber-700' },
    upload_image: { label: t('comp_media_action_upload_image', 'Upload image'), icon: ImagePlus, className: 'bg-amber-600 text-white hover:bg-amber-700' },
    upload_additional: { label: t('comp_media_action_upload_additional', 'Add more slides'), icon: Plus, className: 'bg-amber-600 text-white hover:bg-amber-700' },
    remove_slide: { label: t('comp_media_action_remove_slide', 'Remove slide'), icon: X, className: 'bg-white text-amber-700 border border-amber-300 hover:bg-amber-100' },
  };

  // Root tint escalates when incompatibles are present — user should see
  // at a glance that a subset of issues is a hard block, not just "oh,
  // more stuff to fix".
  const rootBorder = incompatibleCount > 0 ? 'border-red-200' : 'border-amber-200';
  const rootBg = incompatibleCount > 0 ? 'bg-red-50' : 'bg-amber-50';
  const rootIconColor = incompatibleCount > 0 ? 'text-red-600' : 'text-amber-600';
  const rootTextColor = incompatibleCount > 0 ? 'text-red-800' : 'text-amber-800';
  const rootChevronColor = incompatibleCount > 0 ? 'text-red-500' : 'text-amber-500';

  return (
    <div className={`rounded-lg border ${rootBorder} ${rootBg} overflow-hidden`}>
      {/* Header */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left"
      >
        <AlertTriangle className={`w-3.5 h-3.5 ${rootIconColor} flex-shrink-0`} />
        <span className={`flex-1 text-xs font-semibold ${rootTextColor}`}>
          {t('comp_media_issues_found', '{{count}} media issue(s) detected', { count: totalIssues })}
          {hasPendingTasks && (
            <span className="font-normal text-amber-600 ml-1">
              — {t('comp_media_tasks_running', '{{count}} fix(es) in progress', { count: pendingTasks.length })}
            </span>
          )}
        </span>
        {/* Three-bucket pills */}
        <span className="flex items-center gap-1 mr-1">
          {autoFixCount > 0 && (
            <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-200 text-amber-800">
              {t('comp_media_bucket_auto', '{{count}} auto', { count: autoFixCount })}
            </span>
          )}
          {userActionCount > 0 && (
            <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">
              {t('comp_media_bucket_user', '{{count}} input', { count: userActionCount })}
            </span>
          )}
          {incompatibleCount > 0 && (
            <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-red-100 text-red-800">
              {t('comp_media_bucket_incompat', '{{count}} blocked', { count: incompatibleCount })}
            </span>
          )}
        </span>
        {expanded ? (
          <ChevronUp className={`w-3.5 h-3.5 ${rootChevronColor}`} />
        ) : (
          <ChevronDown className={`w-3.5 h-3.5 ${rootChevronColor}`} />
        )}
      </button>

      {/* Pending tasks indicator */}
      {hasPendingTasks && expanded && (
        <div className="border-t border-amber-200 px-3 py-2 bg-blue-50 flex items-center gap-2 text-xs text-blue-700">
          <Loader className="w-3 h-3 animate-spin flex-shrink-0" />
          <span>
            {t('comp_media_processing', 'Processing {{count}} media fix(es) in background...', {
              count: pendingTasks.length,
            })}
          </span>
        </div>
      )}

      {/* Issue list */}
      {expanded && (
        <div className="border-t border-amber-200 divide-y divide-amber-100">
          {results.map((result) =>
            result.issues.map((issue, idx) => {
              const mediaItem = doneMedia.find(
                (m) =>
                  resolveEffectiveMediaIdForPlatform(m, result.platform) === result.media_id,
              );
              const isFixing = fixing?.startsWith(`${result.media_id}:${result.platform}`);
              const isTaskPending = pendingTasks.some(
                (t) => t.mediaId === result.media_id && t.platform === result.platform
              );
              const bucket = resolutionOf(issue);
              // Row-level tint so the user instantly sees which rows are
              // hard blocks vs which need a click vs which will auto-fix.
              const rowTint = bucket === 'incompatible'
                ? 'bg-red-50/40'
                : bucket === 'user_action'
                  ? 'bg-blue-50/30'
                  : '';
              const bucketBadge = bucket === 'incompatible'
                ? { label: t('comp_media_row_incompat', 'BLOCKED'), cls: 'bg-red-100 text-red-700' }
                : bucket === 'user_action'
                  ? { label: t('comp_media_row_input', 'INPUT NEEDED'), cls: 'bg-blue-100 text-blue-700' }
                  : { label: t('comp_media_row_auto', 'AUTO-FIX'), cls: 'bg-amber-100 text-amber-700' };

              // Per-issue actions from the backend three-bucket classifier.
              // When absent, fall back to derived defaults per bucket so the
              // UI still lets the user act on older responses.
              const actions: MediaResolutionAction[] = issue.actions && issue.actions.length > 0
                ? issue.actions
                : bucket === 'incompatible'
                  ? ['drop_platform', 'change_format']
                  : bucket === 'user_action'
                    ? ['replace_media', 'drop_platform']
                    : [];

              return (
                <div
                  key={`${result.media_id}-${result.platform}-${idx}`}
                  className={`px-3 py-2.5 flex items-start gap-2 ${rowTint}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className={`text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded ${bucketBadge.cls}`}>
                        {bucketBadge.label}
                      </span>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700">
                        {result.platform}
                      </span>
                      <span className="text-amber-300">&middot;</span>
                      <span className="text-[10px] text-amber-600 truncate">
                        {mediaItem?.filename || 'Media'}
                      </span>
                    </div>
                    <p className="text-xs text-amber-800">{issue.message}</p>
                  </div>
                  {/* Already auto-fixed for this platform but still
                      doesn't comply — usually means the selected
                      platforms have conflicting rules (e.g. Instagram's
                      4:5 portrait range vs TikTok's 9:16). Another Pad
                      click would just produce the same output, so we
                      swap the fix buttons for an "upload a fresh
                      version" path and explain the state in plain
                      language. */}
                  {issue.already_auto_fixed && !isTaskPending && (
                    <div className="flex items-start gap-1.5 flex-shrink-0 max-w-[220px]">
                      <Info
                        className="w-3 h-3 text-amber-600 mt-0.5 flex-shrink-0"
                        aria-hidden="true"
                      />
                      <div className="flex flex-col gap-1">
                        <span
                          className="text-[10px] leading-tight text-amber-700"
                          title={t(
                            'comp_media_already_attempted_tooltip',
                            'We already tried to resize this file for {{platform}}, but the result still falls outside the platform\u2019s accepted range. This usually happens when another selected platform requires a conflicting aspect ratio. To fix this, upload a version that is already sized for {{platform}}, or remove {{platform}} from your target platforms.',
                            {
                              platform:
                                result.platform.charAt(0).toUpperCase() +
                                result.platform.slice(1),
                            },
                          )}
                        >
                          {t(
                            'comp_media_already_attempted',
                            'Can\u2019t auto-fix further for {{platform}}. Upload a version sized for {{platform}}, or remove it from your platforms.',
                            {
                              platform:
                                result.platform.charAt(0).toUpperCase() +
                                result.platform.slice(1),
                            },
                          )}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            if (typeof document !== 'undefined') {
                              const el = document.getElementById(
                                'studio-media-section',
                              );
                              el?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'start',
                              });
                            }
                          }}
                          className="inline-flex items-center gap-1 self-start px-1.5 py-0.5 rounded-md text-[10px] font-semibold text-amber-700 bg-amber-100 hover:bg-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-400 transition-colors"
                        >
                          <Upload className="w-3 h-3" aria-hidden="true" />
                          {t(
                            'comp_media_upload_for_platform',
                            'Upload new',
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                  {/* user_action / incompatible: render the action CTAs
                      from issue.actions (or bucket-default fallback).
                      Keeps the "Replace media" path routed through the
                      dispatcher so composers can hook onReplaceMedia. */}
                  {!issue.fixable && !isTaskPending && !issue.already_auto_fixed && bucket !== 'auto_fix' && actions.length > 0 && (
                    <div className="flex items-center gap-1 flex-shrink-0 flex-wrap justify-end">
                      {actions.map((action) => {
                        const meta = actionMeta[action];
                        if (!meta) { return null; }
                        const Icon = meta.icon;
                        return (
                          <button
                            key={action}
                            type="button"
                            onClick={() => dispatchIssueAction(action, result)}
                            className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold transition-colors disabled:opacity-50 ${meta.className}`}
                            title={meta.label}
                          >
                            <Icon className="w-3 h-3" />
                            {meta.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {issue.fixable && !isTaskPending && (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {/* Aspect ratio fixes */}
                      {(issue.fix_strategies || []).includes('pad') && (
                        <button
                          type="button"
                          disabled={!!isFixing}
                          onClick={() =>
                            handleAutoFix(result.media_id, result.platform, 'pad')
                          }
                          className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 transition-colors"
                          title={t('comp_media_fix_pad', 'Add black bars to fit')}
                        >
                          {isFixing && fixing?.endsWith(':pad') ? (
                            <Loader className="w-3 h-3 animate-spin" />
                          ) : (
                            <Square className="w-3 h-3" />
                          )}
                          {t('comp_media_pad', 'Pad')}
                        </button>
                      )}
                      {(issue.fix_strategies || []).includes('crop') && (
                        <button
                          type="button"
                          disabled={!!isFixing}
                          onClick={() =>
                            handleAutoFix(result.media_id, result.platform, 'crop')
                          }
                          className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold bg-white text-amber-700 border border-amber-300 hover:bg-amber-100 disabled:opacity-50 transition-colors"
                          title={t('comp_media_fix_crop', 'Center-crop to fit')}
                        >
                          {isFixing && fixing?.endsWith(':crop') ? (
                            <Loader className="w-3 h-3 animate-spin" />
                          ) : (
                            <Crop className="w-3 h-3" />
                          )}
                          {t('comp_media_crop', 'Crop')}
                        </button>
                      )}
                      {/* Video duration trim */}
                      {(issue.fix_strategies || []).includes('trim') && (
                        <button
                          type="button"
                          disabled={!!isFixing}
                          onClick={() =>
                            handleAutoFix(result.media_id, result.platform, 'trim')
                          }
                          className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50 transition-colors"
                          title={t(
                            'comp_media_fix_trim',
                            'Trim video to the platform limit',
                          )}
                        >
                          {isFixing && fixing?.endsWith(':trim') ? (
                            <Loader className="w-3 h-3 animate-spin" />
                          ) : (
                            <Scissors className="w-3 h-3" />
                          )}
                          {t('comp_media_trim', 'Trim')}
                        </button>
                      )}
                      {/* Video re-encode */}
                      {(issue.fix_strategies || []).includes('re_encode') && (
                        <button
                          type="button"
                          disabled={!!isFixing}
                          onClick={() =>
                            handleReEncode(result.media_id, result.platform)
                          }
                          className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
                          title={t('comp_media_fix_reencode', 'Re-encode video to reduce size')}
                        >
                          {isFixing && fixing?.endsWith(':re_encode') ? (
                            <Loader className="w-3 h-3 animate-spin" />
                          ) : (
                            <HardDrive className="w-3 h-3" />
                          )}
                          {t('comp_media_reencode', 'Compress')}
                        </button>
                      )}
                    </div>
                  )}
                  {isTaskPending && (
                    <div className="flex items-center gap-1 flex-shrink-0 text-blue-600">
                      <Loader className="w-3 h-3 animate-spin" />
                      <span className="text-[10px] font-medium">
                        {t('comp_media_in_progress', 'Processing...')}
                      </span>
                    </div>
                  )}
                </div>
              );
            })
          )}

          {/* Bulk auto-fix button with per-item progress.
              Key differences from the old implementation:
                - Iterates every (media × platform) pair with a fixable
                  issue, not just the first per media. Multiple issues on
                  the same media still get fully covered.
                - Each iteration is wrapped in try/catch and failures are
                  collected + reported at the end instead of aborting
                  the loop silently.
                - Progress state drives a visible "Fixing N of M" bar so
                  the user can tell the operation is running (and isn't
                  just a stuck spinner). */}
          {fixableIssues.length > 1 && !hasPendingTasks && !bulkProgress && (
            <div className="px-3 py-2 bg-amber-100/50">
              <button
                type="button"
                disabled={!!fixing}
                onClick={async () => {
                  // Flatten every fixable (media_id, platform) pair.
                  // We de-dupe by that compound key (not media_id alone)
                  // because the same media can have distinct issues per
                  // platform; each needs its own fix pass.
                  const queue: Array<{
                    mediaId: string;
                    platform: string;
                    strategies: string[];
                    label: string;
                  }> = [];
                  const seen = new Set<string>();
                  for (const result of fixableIssues) {
                    const fixable = result.issues.find((i) => i.fixable);
                    if (!fixable) { continue; }
                    const key = `${result.media_id}:${result.platform}`;
                    if (seen.has(key)) { continue; }
                    seen.add(key);
                    const mediaItem = doneMedia.find(
                      (m) => m.mediaId === result.media_id,
                    );
                    queue.push({
                      mediaId: result.media_id,
                      platform: result.platform,
                      strategies: fixable.fix_strategies || ['pad'],
                      label: `${mediaItem?.filename || 'Media'} · ${result.platform}`,
                    });
                  }

                  if (queue.length === 0) {
                    return;
                  }

                  setBulkSummary(null);
                  setBulkProgress({
                    done: 0,
                    total: queue.length,
                    currentLabel: queue[0]?.label ?? '',
                  });

                  let succeeded = 0;
                  let failed = 0;
                  let skipped = 0;
                  const failures: Array<{ label: string; reason: string }> = [];

                  for (let i = 0; i < queue.length; i++) {
                    const item = queue[i]!;
                    setBulkProgress({
                      done: i,
                      total: queue.length,
                      currentLabel: item.label,
                    });

                    try {
                      if (item.strategies.includes('re_encode')) {
                        await handleReEncode(item.mediaId, item.platform, {
                          silent: true,
                        });
                      } else if (item.strategies.includes('trim')) {
                        await handleAutoFix(
                          item.mediaId,
                          item.platform,
                          'trim',
                          { silent: true },
                        );
                      } else if (item.strategies.includes('pad')) {
                        await handleAutoFix(
                          item.mediaId,
                          item.platform,
                          'pad',
                          { silent: true },
                        );
                      } else {
                        // Nothing we can do here — flag as skipped.
                        skipped++;
                        continue;
                      }
                      succeeded++;
                    } catch (err) {
                      failed++;
                      failures.push({
                        label: item.label,
                        reason: getErrorMessage(
                          err,
                          t('comp_media_fix_failed', 'Failed to optimize media'),
                        ),
                      });
                    }
                  }

                  setBulkProgress(null);
                  setBulkSummary({ succeeded, failed, skipped, failures });

                  if (succeeded > 0 && failed === 0) {
                    toast.success(
                      t(
                        'comp_media_bulk_all_fixed',
                        '{{count}} media fix(es) applied',
                        { count: succeeded },
                      ),
                    );
                  } else if (succeeded > 0 && failed > 0) {
                    toast(
                      t(
                        'comp_media_bulk_partial',
                        '{{ok}} fixed, {{bad}} failed',
                        { ok: succeeded, bad: failed },
                      ),
                      { icon: '\u26A0\uFE0F', duration: 6000 },
                    );
                  } else if (failed > 0) {
                    toast.error(
                      t(
                        'comp_media_bulk_all_failed',
                        '{{count}} fix(es) failed',
                        { count: failed },
                      ),
                    );
                  }
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 transition-colors"
              >
                <Wand2 className="w-3.5 h-3.5" />
                {t('comp_media_fix_all', 'Auto-fix all')}
                <span className="ml-1 text-[10px] font-normal opacity-80">
                  ({fixableIssues.length})
                </span>
              </button>
            </div>
          )}

          {/* Live progress bar while bulk fix is running. */}
          {bulkProgress && (
            <div
              className="px-3 py-2 bg-amber-100/60 border-t border-amber-200"
              role="status"
              aria-live="polite"
            >
              <div className="flex items-center gap-2 text-[11px] text-amber-800">
                <Loader
                  className="w-3 h-3 animate-spin flex-shrink-0"
                  aria-hidden="true"
                />
                <span className="flex-1 truncate">
                  {t(
                    'comp_media_bulk_progress',
                    'Fixing {{done}} of {{total}}: {{label}}',
                    {
                      done: bulkProgress.done + 1,
                      total: bulkProgress.total,
                      label: bulkProgress.currentLabel,
                    },
                  )}
                </span>
              </div>
              <div
                className="w-full h-1 mt-1.5 rounded-full bg-amber-200 overflow-hidden"
                role="progressbar"
                aria-valuenow={bulkProgress.done}
                aria-valuemax={bulkProgress.total}
                aria-valuemin={0}
              >
                <div
                  className="h-full bg-amber-600 transition-all duration-300"
                  style={{
                    width: `${(bulkProgress.done / bulkProgress.total) * 100}%`,
                  }}
                />
              </div>
            </div>
          )}

          {/* One-shot summary rendered after the bulk run completes.
              Auto-clears when the user takes the next meaningful action
              (e.g. another fix, or compliance results change). */}
          {bulkSummary && !bulkProgress && (
            <div className="px-3 py-2 bg-amber-100/40 border-t border-amber-200 text-[11px]">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  {bulkSummary.failed === 0 && bulkSummary.skipped === 0 && (
                    <div className="flex items-center gap-1.5 text-emerald-700">
                      <Check className="w-3 h-3" aria-hidden="true" />
                      {t(
                        'comp_media_bulk_summary_all_ok',
                        'All {{count}} fix(es) applied. Re-checking compliance…',
                        { count: bulkSummary.succeeded },
                      )}
                    </div>
                  )}
                  {(bulkSummary.failed > 0 || bulkSummary.skipped > 0) && (
                    <>
                      <div className="font-semibold text-amber-800">
                        {t(
                          'comp_media_bulk_summary_partial',
                          '{{ok}} succeeded · {{bad}} failed · {{skip}} skipped',
                          {
                            ok: bulkSummary.succeeded,
                            bad: bulkSummary.failed,
                            skip: bulkSummary.skipped,
                          },
                        )}
                      </div>
                      {bulkSummary.failures.slice(0, 3).map((f) => (
                        <div
                          key={f.label}
                          className="flex items-start gap-1 mt-1 text-amber-700"
                        >
                          <span className="text-red-500">•</span>
                          <span className="flex-1">
                            <strong>{f.label}:</strong> {f.reason}
                          </span>
                        </div>
                      ))}
                      {bulkSummary.failures.length > 3 && (
                        <div className="text-amber-600 mt-0.5">
                          {t(
                            'comp_media_bulk_summary_more',
                            '+{{count}} more',
                            { count: bulkSummary.failures.length - 3 },
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setBulkSummary(null)}
                  aria-label={t('comp_media_bulk_summary_dismiss', 'Dismiss')}
                  className="flex-shrink-0 w-5 h-5 rounded-md flex items-center justify-center text-amber-600 hover:bg-amber-200"
                >
                  &times;
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
